import type {
  NamedSupSportDetail,
  SurfDetail,
  SportDetail,
  SkiDetail,
} from "./detail";
import type { SurfHourlyData } from "./surf-hourly-detail";
import type { ConditionNote } from "./condition-notes";
import type { SatelliteWaterQuality } from "./satellite-water-quality";
import type { WaterQualityData } from "./water-quality";
import type { LocationTimingData } from "./timing-data";
import type { ActiveBusyPeriod } from "./busy-periods";
import type { CheckinSummary } from "./checkins";
import type { NormalisedHour, StoredHourlyScore } from "./daily-obs-types";
import type { HeatmapDataset } from "@/app/_components/conditions/SeasonHeatmap";
import type { SwellForecast } from "./swell-forecast";
import type { SpotComparisonResult } from "./spot-comparison";
import type { SessionRow, ConditionsSnapshot } from "@/app/actions/sessions";
import type { SpotTip } from "@/app/actions/tips";
import type { SpotAccuracy } from "./forecast-accuracy";
import type { MonthlyStats } from "./historical-stats";
import type { TouringDailyResult } from "./ski-touring-scoring-v4";
import type { AirQualityData } from "./air-quality";
import type { SkiElevationData } from "./ski-elevation";
import type { PriceContext } from "./pricing";
import type { ResortMetrics } from "./metrics";
import type { SkiHourlyReading } from "./open-meteo";
import type { SessionWindowResult, HourlyScore } from "./session-window-universal";
import type {
  SafetyExplanationSeverity,
  SafetyFactorExplanation,
  SportScoreExplanation,
} from "./factor-explanations";
import type { ScoreEvidenceTransport } from "./score-evidence";
import type { SupProfileHourlyScore } from "./sup-profile-transport";
import type { TrendResult } from "./trend";
import type { RarityResult } from "./rarity";
import type { SecondarySwellResult, SwellTrajectory } from "./swell-analysis";
import type { KiteSizeReference, LaunchSafety, ThermalPrediction, GustConsistency } from "./wind-analysis";
import type { SurgeResult, BoatFeasibilityV2, PressureTrendResult } from "./diving-analysis";
import type { ExitWindChillResult, JellyfishResult, SwimDifficulty } from "./swim-analysis";
import type { FlatwaterWindow, PaddleDifficulty, SupSessionType, ReturnJourneyRisk } from "./sup-analysis";
import type { AspectWindLoading, PowderDecayResult } from "./aspect-analysis";
import type { TrailDryingResult, GoldenHourWindow } from "./trail-analysis";
import type { SeasonStatus } from "./season-status";
import type { GroundTruthSummary } from "./ground-truth-summary";
import type { BeachGradient, CoastalBreakType } from "./visualiser/coastal-types";

// ---------------------------------------------------------------------------
// Location metadata (future DB column on condition_locations)
// ---------------------------------------------------------------------------

export type LocationMetadata = {
  reef_type?: "reef" | "beach" | "point" | "river_mouth" | "slab";
  exposure?: "exposed" | "semi_exposed" | "sheltered";
  seabed?: "sand" | "rock" | "coral" | "mixed";
  best_season?: string;
  typical_conditions?: string;
  hazards?: string[];
  facilities?: string[];
  access_notes?: string;
  parking?: string;
};

// ---------------------------------------------------------------------------
// Sport badge (multi-sport score pills)
// ---------------------------------------------------------------------------

export type SportBadge = {
  sport: string; // "surf", "wind", "foil", "downwind-foil", "wing-foil", "diving", "swimming", "sup", "piste", "off-piste", "touring", "mtb"
  score: number | null;
  label: string | null;
  isPrimary: boolean;
  offSeason?: boolean;
  /** The score and label are the previous run's forecast for today (the nightly gap), not today's score. */
  forecast?: boolean;
  /** With `forecast`: the forecast day's caution (amber, not assessed, avalanche bulletin), shown with it. */
  forecastCaution?: string;
};

export type PerSportSeasonStatus = {
  ski: SeasonStatus;
  touring: SeasonStatus | null;
  mtb: SeasonStatus | null;
};

// ---------------------------------------------------------------------------
// Sport URL key mappings
// ---------------------------------------------------------------------------

/** Internal key -> URL param mapping. SummerSport "swim" maps to URL "swimming". */
export const SPORT_URL_KEY: Record<string, string> = {
  surf: "surf",
  wind: "wind",
  foil: "foil",
  "downwind-foil": "downwind-foil",
  "wing-foil": "wing-foil",
  diving: "diving",
  swim: "swimming",
  swimming: "swimming",
  sup: "sup",
  "sup-touring": "sup-touring",
  "sup-surf": "sup-surf",
  "sup-downwind": "sup-downwind",
  piste: "piste",
  "off-piste": "off-piste",
  touring: "touring",
  mtb: "mtb",
  bodyboarding: "bodyboarding",
  "sailing-club": "sailing",
  "sailing-hp": "sailing",
};

/** URL param -> internal key mapping. URL "swimming" maps back to "swim". */
export const URL_TO_INTERNAL_SPORT: Record<string, string> = {
  surf: "surf",
  wind: "wind",
  foil: "foil",
  "downwind-foil": "downwind-foil",
  "wing-foil": "wing-foil",
  diving: "diving",
  swimming: "swim",
  sup: "sup-touring",
  "sup-touring": "sup-touring",
  "sup-surf": "sup-surf",
  "sup-downwind": "sup-downwind",
  piste: "piste",
  "off-piste": "off-piste",
  touring: "touring",
  mtb: "mtb",
  bodyboarding: "bodyboarding",
  sailing: "sailing-club",
};

// ---------------------------------------------------------------------------
// Spot guide row (queried directly from supabase)
// ---------------------------------------------------------------------------

export type SpotGuideRow = {
  content_md: string;
  author_type: string;
} | null;

// ---------------------------------------------------------------------------
// Crew data for condition spot CTA
// ---------------------------------------------------------------------------

// Public crew info only. Whether the current user is a member is fetched
// client-side (/api/conditions/user-state) so the page stays cacheable.
export type CrewData = {
  id: string;
  slug: string;
  destinationName: string;
  memberCount: number;
} | null;

type UnifiedSportScoreExplanation<
  TPoint extends StoredHourlyScore,
> = [Extract<TPoint, SupProfileHourlyScore>] extends [never]
  ? SportScoreExplanation
  : SportScoreExplanation<SafetyFactorExplanation<SafetyExplanationSeverity>>;

// ---------------------------------------------------------------------------
// Unified coastal data
// ---------------------------------------------------------------------------

export type UnifiedCoastalData<
  TPoint extends StoredHourlyScore = HourlyScore,
> = {
  // Location core
  location: {
    id: string;
    slug: string;
    name: string;
    country: string;
    countryCode: string;
    region: string;
    lat: number;
    lng: number;
    localityLongitude?: number | null;
    timezone: string | null;
    localDate: string;
    description: string | null;
    guideSlug: string | null;
    jobsFilter: string | null;
    spotType: CoastalBreakType | null;
    tidePreference: string | null;
    difficultyLevel: string | null;
    shelterLevel: string | null;
    beachFacingDeg: number | null;
    beachGradient: BeachGradient | null;
    exposedSectors: [number, number][] | null;
    sweetSpotMinM: number | null;
    sweetSpotMaxM: number | null;
    locationMetadata: LocationMetadata | null;
    bottomType: string | null;
    marineHighlights: string | null;
    waterType: string | null;
    prevailingWindDir: string | null;
    typicalVisibility: string | null;
    maxWaveSize: string | null;
    bestWindDirection: string | null;
    lifeguarded: string | null;
    nearbyCsoCount: number;
  };

  // Sport details
  surfDetail: SurfDetail;
  windDetail: SportDetail | null;
  foilDetail: SportDetail | null;
  downwindFoilDetail: SportDetail | null;
  wingFoilDetail: SportDetail | null;
  divingDetail: SportDetail | null;
  swimDetail: SportDetail | null;
  supDetail: SportDetail | null;
  supTouringDetail: NamedSupSportDetail | null;
  supSurfDetail: NamedSupSportDetail | null;
  supDownwindDetail: NamedSupSportDetail | null;
  bodyboardingDetail: SportDetail | null;

  // Multi-sport badges
  badges: SportBadge[];

  // Primary state
  lastUpdated: string;
  /** Request-time verdict: unified-detail-freshness.ts recomputes it from scoreDate and lastUpdated. */
  isStale: boolean;
  /**
   * The spot-local date of the stored score row behind every sport detail (condition_scores.date).
   * With lastUpdated it is judged on the request clock (score-freshness.ts scoreRowStateAt), so a
   * cached page never keeps a stale verdict. Absent only on payloads cached before the field existed.
   */
  scoreDate?: string | null;
  /**
   * Request-time only (never cached): set during the nightly gap, when the badges carry the stored
   * row's forecast for the resort's new local date (lib/conditions/gap-forecast.ts).
   */
  gapForecast?: import("./gap-forecast-display").GapForecastInfo | null;
  isBusyPeriod: boolean;
  busyPeriodLabel: string | null;
  weatherCode: number;

  // Shared data
  hourlyData: SurfHourlyData | null;
  conditionNotes: Record<string, ConditionNote>;
  waterQuality: WaterQualityData | null;
  satelliteWQ: SatelliteWaterQuality | null;
  heatmapDatasets: HeatmapDataset[];
  busyPeriods: ActiveBusyPeriod[];
  checkinSummary: CheckinSummary;

  // Timing
  timingPrimary: LocationTimingData | null;
  timingAlternate: LocationTimingData | null;

  // Raw hourly JSONB from daily_observations
  hourlyJsonb: NormalisedHour[] | null;

  // Bottom-section data (preserved from current page)
  swellForecast: SwellForecast | null;
  nearbySpots: SpotComparisonResult | null;
  recentSessions: SessionRow[];
  spotTips: SpotTip[];
  spotGuide: SpotGuideRow;
  historicalStats: MonthlyStats[];
  forecastAccuracy: SpotAccuracy | null;
  groundTruthSummaries: Record<string, GroundTruthSummary>;
  conditionsSnapshot: ConditionsSnapshot;
  crewData: CrewData;
  airQuality: AirQualityData | null;

  // Per-sport hourly scores (computed from NormalisedHour[])
  sportHourlyScores: Record<string, TPoint[]>;
  sportScoreExplanations: Record<string, UnifiedSportScoreExplanation<TPoint>>;
  sportScoreEvidence: Record<string, ScoreEvidenceTransport>;
  yesterdaySportScores: Record<string, TPoint[]>;
  tomorrowSportScores: Record<string, TPoint[]>;

  // Adjacent-day hourly JSONB for day toggle
  yesterdayJsonb: NormalisedHour[] | null;
  tomorrowJsonb: NormalisedHour[] | null;

  // Enhancements (from sport-enrichment pipeline)
  universalEnhancements: Record<string, UniversalEnhancements>;
  surfEnhancements: SurfEnhancements | null;
  windEnhancements: WindEnhancements | null;
  divingEnhancements: DivingEnhancements | null;
  swimEnhancements: SwimEnhancements | null;
  supEnhancements: SupEnhancements | null;

  // Public holiday dates for the 7-day forecast window (YYYY-MM-DD strings)
  publicHolidayDates: string[];

  /** Why a shown sport is not ranked here; absent unless a listed sport's venue is ineligible. */
  sportEligibilityNotices?: SportEligibilityNotices;
};

/**
 * Spot page notices by internal sport key (P2C-WP9-05): `sportEligibilityNotice` copy for each
 * shown sport that CONDITIONS_SPORT_ELIGIBILITY_SPORTS lists and whose venue is ineligible. The
 * score itself stays; the notice says it is not ranked.
 */
export type SportEligibilityNotices = Readonly<Partial<Record<string, string>>>;

// ---------------------------------------------------------------------------
// Unified ski data
// ---------------------------------------------------------------------------

export type UnifiedSkiData = {
  mountainExplorer?: import("../mountain-explorer/types").MountainForecast;
  // Location core (includes elevation fields)
  location: {
    id: string;
    slug: string;
    name: string;
    country: string;
    countryCode: string;
    region: string;
    lat: number;
    lng: number;
    timezone: string | null;
    localDate: string;
    description: string | null;
    guideSlug: string | null;
    jobsFilter: string | null;
    glacier: boolean;
    snowPark: boolean;
    bikePark: boolean;
    upliftAvailable: boolean;
    beginnerFriendly: boolean;
    mtbDifficulty: string | null;
    terrainType: string | null;
    treelineTerrain: "none" | "limited" | "extensive" | null;
    elevationM: number | null;
    baseElevationM: number | null;
    midElevationM: number | null;
  };

  // Sport details
  skiDetail: SkiDetail;
  mtbDetail: SportDetail | null;

  // Touring result (full computation output from calculateSkiTouringDaily)
  touringResult: TouringDailyResult | null;
  touringSunrise: number | null;
  touringSunset: number | null;

  // Elevation-band sub-scores (read-time derivation, same pattern as piste)
  offPisteElevationBands: import("./offpiste-scoring-v5").OffPisteElevationBand[] | null;
  // Off-piste daily sub-factors (read-time derivation) feeding the radar
  offPisteFactors: Record<string, number> | null;
  touringElevationBands: import("./ski-touring-scoring-v4").TouringElevationBand[] | null;
  // The four legacy fields above are read-time legacy engine output: they are null once
  // revision 3 is published (SKI_SCORING_ENGINE=v3). Use skiV3 instead.

  /**
   * Ski v3 fields per sport, read from stored carriers (score_provenance and today's forecast
   * day): status and reason, confidence class, P10 and P90 (0 to 100), piste extent, band
   * summaries (StoredSkiBand[]), surface time blocks, the touring plan and the off-piste rose.
   * Empty for revision 2 rows. Optional so payloads cached before this field still type check.
   */
  skiV3?: import("./ski-v3-carriers").SkiV3BySport;
  /** Operator's official lift and run status page (display only), when known. */
  officialStatusLink?: import("./ski/official-status-links").OfficialStatusLink | null;

  // Multi-sport badges
  badges: SportBadge[];

  // Air quality
  airQuality: AirQualityData | null;

  // Resort metrics (from resort_metrics table)
  resortMetrics: ResortMetrics | null;

  // Price context (from lift_pass_prices)
  priceContext: PriceContext | null;

  // Quality rank position
  qualityRank: { rank: number; total: number } | null;

  // Elevation data (from buildSkiElevationData)
  elevationData: SkiElevationData | null;

  skiHourly: SkiHourlyReading[];

  // Shared data
  conditionNotes: Record<string, ConditionNote>;
  heatmapDatasets: HeatmapDataset[];
  checkinSummary: CheckinSummary;
  busyPeriods: ActiveBusyPeriod[];
  groundTruthSummaries: Record<string, GroundTruthSummary>;
  forecastAccuracy: SpotAccuracy | null;

  // Timing
  timingPrimary: LocationTimingData | null;
  timingAlternate: LocationTimingData | null;

  // Primary state
  lastUpdated: string;
  /** Request-time verdict: unified-detail-freshness.ts recomputes it from scoreDate and lastUpdated. */
  isStale: boolean;
  /**
   * The spot-local date of the stored score row behind every sport detail (condition_scores.date).
   * With lastUpdated it is judged on the request clock (score-freshness.ts scoreRowStateAt), so a
   * cached page never keeps a stale verdict. Absent only on payloads cached before the field existed.
   */
  scoreDate?: string | null;
  /**
   * Request-time only (never cached): set during the nightly gap, when the badges carry the stored
   * row's forecast for the resort's new local date (lib/conditions/gap-forecast.ts).
   */
  gapForecast?: import("./gap-forecast-display").GapForecastInfo | null;
  isBusyPeriod: boolean;
  busyPeriodLabel: string | null;
  weatherCode: number;

  // Raw hourly JSONB from daily_observations
  hourlyJsonb: NormalisedHour[] | null;
  yesterdayJsonb: NormalisedHour[] | null;
  tomorrowJsonb: NormalisedHour[] | null;

  // Per-sport hourly scores (computed from NormalisedHour[])
  sportHourlyScores: Record<string, HourlyScore[]>;
  sportScoreExplanations: Record<string, SportScoreExplanation>;
  sportScoreEvidence: Record<string, ScoreEvidenceTransport>;
  yesterdaySportScores: Record<string, HourlyScore[]>;
  tomorrowSportScores: Record<string, HourlyScore[]>;

  // Season status (date-based open/closed/shoulder)
  seasonStatus: SeasonStatus;
  perSportSeasonStatus: PerSportSeasonStatus;
  defaultSport: string;

  // Enhancements
  universalEnhancements: Record<string, UniversalEnhancements>;
  offPisteEnhancements: OffPisteEnhancements | null;
  touringEnhancements: TouringEnhancements | null;
  mtbEnhancements: MtbEnhancements | null;

  // Public holiday dates for the 7-day forecast window (YYYY-MM-DD strings)
  publicHolidayDates: string[];

  /** Why a shown sport (mtb) is not ranked here; absent unless its venue is ineligible under the flag. */
  sportEligibilityNotices?: SportEligibilityNotices;
};

// ---------------------------------------------------------------------------
// Enhancement types (computed by sport-enrichment pipeline)
// ---------------------------------------------------------------------------

export type UniversalEnhancements = {
  sessionWindow: SessionWindowResult | null;
  trend: TrendResult | null;
  rarity: RarityResult | null;
};

export type SurfEnhancements = {
  secondarySwells: SecondarySwellResult | null;
  swellTrajectory: SwellTrajectory | null;
  swellCharacter: string | null;
  boardReference: string | null;
  dawnPatrol: { time: string; isGlassy: boolean; isUncrowded: boolean } | null;
};

export type WindEnhancements = {
  kiteSizeRef: KiteSizeReference | null;
  launchSafety: LaunchSafety | null;
  thermal: ThermalPrediction | null;
  gustConsistency: GustConsistency | null;
  /** Lightning hazard flag; independent of the score (storm policy). */
  stormFlag: { level: "amber" | "red"; label: string; detail: string } | null;
};

export type DivingEnhancements = {
  surge: SurgeResult | null;
  /**
   * Revision 1 cards are always a BoatFeasibility. Once diving publishes revision 2 the route's
   * carrier writes assessBoatFeasibilityV2, which reads "unavailable" (grey) when the sea state
   * or gust is missing (P2C-WP5-08; WP-S4 DIVSWIM-3).
   */
  boatFeasibility: BoatFeasibilityV2 | null;
  pressureTrend: PressureTrendResult | null;
  moonContext: string | null;
};

export type SwimEnhancements = {
  exitWindChill: ExitWindChillResult | null;
  jellyfish: JellyfishResult | null;
  difficulty: SwimDifficulty | null;
  dawnSwim: { time: string; waterTempC: number; isCalm: boolean } | null;
};

export type SupEnhancements = {
  flatwaterWindow: FlatwaterWindow | null;
  paddleDifficulty: PaddleDifficulty | null;
  sessionType: SupSessionType | null;
  returnRisk: ReturnJourneyRisk | null;
  uvAlert: { index: number; label: string } | null;
};

export type OffPisteEnhancements = {
  windLoading: AspectWindLoading[] | null;
  powderDecay: PowderDecayResult | null;
  snowTypeByAspect: { aspect: string; snowType: string; description: string }[] | null;
  crustRisk: {
    detected: boolean;
    severity: number;
    label: string | null;
    detail: string | null;
    maxDaytimeTempC: number | null;
    minOvernightTempC: number | null;
    hoursSinceLastCoverage: number | null;
  } | null;
  aspectGuidance: {
    show: boolean;
    bestAspects: string[];
    okAspects: string[];
    avoidAspects: string[];
    windSummary: string | null;
    solarSummary: string | null;
    cornNote: string | null;
  } | null;
  treelineNote: {
    tone: "positive" | "caution" | "negative";
    text: string;
  } | null;
};

export type TouringEnhancements = {
  snowpackWarningHour: number | null;
  skinningEffort: { feelsLikeC: number; label: string } | null;
  descentQuality: { aspect: string; snowType: string; description: string }[] | null;
};

export type MtbEnhancements = {
  trailDrying: TrailDryingResult | null;
  goldenHour: GoldenHourWindow | null;
};
